/**
 * ManagedSqlDatabasesPage Component
 *
 * Main page for managing SQL databases with search and status filter.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { ManagedSqlDbTable } from '../components/ManagedSqlDbTable';
import { CreateManagedSqlDbModal } from '../components/CreateManagedSqlDbModal';
import { UpdateManagedSqlDbModal } from '../components/UpdateManagedSqlDbModal';
import { DeleteManagedSqlDbModal } from '../components/DeleteManagedSqlDbModal';
import { ManagedSqlDbDetailsModal } from '../components/ManagedSqlDbDetailsModal';
import { useManagedSqlDbs } from '../hooks/useManagedSqlDbs';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { ManagedSqlDbSanitized } from '../../../types/managed-sql-db.types';
import { ManagedSqlDbStatus } from '../../../types/managed-sql-db.types';
import { getErrorMessage } from '../../../lib/utils/errors';

type StatusFilterOption = 'all' | ManagedSqlDbStatus;

const STATUS_OPTIONS: { value: StatusFilterOption; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: ManagedSqlDbStatus.CREATING, label: 'Creating' },
  { value: ManagedSqlDbStatus.AVAILABLE, label: 'Available' },
  { value: ManagedSqlDbStatus.UPDATING, label: 'Updating' },
  { value: ManagedSqlDbStatus.DELETING, label: 'Deleting' },
  { value: ManagedSqlDbStatus.FAILED, label: 'Failed' },
];

/**
 * ManagedSqlDatabasesPage component
 */
export const ManagedSqlDatabasesPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();

  // UI State
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption>('all');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedDb, setSelectedDb] = useState<ManagedSqlDbSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'data:managed-db:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'data:managed-db:update');
  const canDelete = hasPermission(activeRole?.permissions, 'data:managed-db:delete');

  // Debounced search (300ms delay)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch managed SQL databases
  const {
    data: managedSqlDbs = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useManagedSqlDbs(tenantId, {
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  // Filter databases locally (search)
  const filteredDbs = useMemo(() => {
    if (!managedSqlDbs) return [];

    let filtered = [...managedSqlDbs];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.managedSqlDbName.toLowerCase().includes(query) ||
          item.managedSqlDbCode.toLowerCase().includes(query) ||
          (item.description && item.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [managedSqlDbs, debouncedSearch]);

  // Handlers
  const handleViewDetails = (db: ManagedSqlDbSanitized) => {
    setSelectedDb(db);
    setShowDetailsModal(true);
  };

  const handleEdit = (db: ManagedSqlDbSanitized) => {
    setSelectedDb(db);
    setShowUpdateModal(true);
  };

  const handleDelete = (db: ManagedSqlDbSanitized) => {
    setSelectedDb(db);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowDetailsModal(false);
    setShowUpdateModal(false);
    setShowDeleteModal(false);
    setSelectedDb(null);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Managed SQL Databases</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              PostgreSQL databases with IAM-enforced access control
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>Create Database</Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search databases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilterOption)}
            className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Databases Table */}
        <ManagedSqlDbTable
          managedSqlDbs={filteredDbs}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canUpdate}
          canDelete={canDelete}
        />

        {/* Create Modal */}
        <CreateManagedSqlDbModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {/* Details Modal */}
        {showDetailsModal && selectedDb && (
          <ManagedSqlDbDetailsModal
            isOpen={showDetailsModal}
            onClose={closeModals}
            tenantId={tenantId}
            managedSqlDbId={selectedDb.managedSqlDbId}
          />
        )}

        {/* Update Modal */}
        {showUpdateModal && selectedDb && (
          <UpdateManagedSqlDbModal
            isOpen={showUpdateModal}
            onClose={closeModals}
            tenantId={tenantId}
            managedSqlDb={selectedDb}
          />
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedDb && (
          <DeleteManagedSqlDbModal
            isOpen={showDeleteModal}
            onClose={closeModals}
            tenantId={tenantId}
            managedSqlDb={selectedDb}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
